import { DEFAULT_TRANSCRIPT_VIEWER_SETTINGS } from './types.js';
/** Where the web app persists the reader's light/dark/system choice. */
export const APP_SETTINGS_KEY = 'polygraf_app_settings';
/**
 * Window event the native shell dispatches after it rewrites the stored theme
 * while a viewer is open. localStorage's own `storage` event never fires in the
 * document that wrote it, so the viewer listens for this instead.
 */
export const APP_THEME_CHANGED_EVENT = 'polygraf-app-theme-changed';
/**
 * Resolve the `data-theme` for the stored app settings.
 *
 * `light`/`dark` are explicit, `system` follows the device. The unset case is
 * the one that differs by surface: the web app has always opened light, so it
 * stays light there (`unsetFollowsSystem: false`); inside the apps' WebView
 * nothing ever writes `polygraf_app_settings`, so "unset" would pin a dark
 * phone to light for good — there it follows the system instead.
 */
export function resolveAppTheme(rawAppSettings, systemPrefersDark, unsetFollowsSystem) {
    let pref;
    try {
        pref = rawAppSettings ? JSON.parse(rawAppSettings)?.theme : null;
    }
    catch {
        pref = null;
    }
    if (pref === 'dark')
        return 'polygraf-dark';
    if (pref === 'light')
        return 'polygraf';
    if (pref === 'system' || unsetFollowsSystem)
        return systemPrefersDark ? 'polygraf-dark' : 'polygraf';
    return 'polygraf';
}
/**
 * The picker option that matches what `resolveAppTheme` does for these stored
 * settings. Unset is not a fourth option: it behaves as `light` on the web and
 * as `system` in the apps' WebView, so Settings shows that one as selected.
 */
export function effectiveThemeChoice(rawAppSettings, unsetFollowsSystem) {
    let pref;
    try {
        pref = rawAppSettings ? JSON.parse(rawAppSettings)?.theme : null;
    }
    catch {
        pref = null;
    }
    if (pref === 'light' || pref === 'dark' || pref === 'system')
        return pref;
    return unsetFollowsSystem ? 'system' : 'light';
}
/**
 * Transcript colours under the dark theme for a reader who never picked their
 * own. The ground is `.viewer-surface`'s dark colour (#0d1117) on purpose: the
 * image viewer paints that same surface, and MU asked for the two viewers to
 * agree (18. 9. 2026). Text is the `dark` preset's #f1f5f9 (about 17:1).
 */
export const DARK_THEME_TRANSCRIPT_COLORS = Object.freeze({
    textColor: '#f1f5f9',
    backgroundColor: '#0d1117',
});
function sameColor(a, b) {
    return typeof a === 'string' && a.trim().toLowerCase() === b;
}
/**
 * Decide the flag for settings read from storage.
 *
 * Payloads written before the flag existed carry the colour pair every viewer
 * persisted on first render. Equal to the old default means the reader never
 * chose, so they follow the theme; anything else was chosen. An explicit
 * boolean always wins. (A reader who tapped the "light" preset is
 * indistinguishable from the default pair and will follow the theme too.)
 */
export function storedColorsFollowTheme(stored) {
    if (typeof stored.colorsFollowTheme === 'boolean')
        return stored.colorsFollowTheme;
    return (sameColor(stored.textColor, DEFAULT_TRANSCRIPT_VIEWER_SETTINGS.textColor) &&
        sameColor(stored.backgroundColor, DEFAULT_TRANSCRIPT_VIEWER_SETTINGS.backgroundColor));
}
/**
 * Add the resolved flag to loaded settings (see `storedColorsFollowTheme`).
 *
 * `stored` is the raw payload from storage. The flag must be read from it, not
 * from `loaded`: loaders spread defaults (which carry `colorsFollowTheme: true`)
 * under the payload, so a payload written before the flag existed would look
 * like an explicit `true` and a reader's own pair would be painted over.
 * The colours are read from `loaded`, after any legibility repair.
 */
export function withColorsFollowTheme(loaded, stored) {
    return {
        ...loaded,
        colorsFollowTheme: storedColorsFollowTheme({
            colorsFollowTheme: stored.colorsFollowTheme,
            textColor: loaded.textColor,
            backgroundColor: loaded.backgroundColor,
        }),
    };
}
/**
 * The settings to paint: the stored ones, with the colours replaced by the
 * theme's pair while the reader has not chosen their own. Pure.
 */
export function effectiveTranscriptSettings(stored, theme) {
    if (!stored.colorsFollowTheme)
        return stored;
    const colors = theme === 'polygraf-dark'
        ? DARK_THEME_TRANSCRIPT_COLORS
        : {
            textColor: DEFAULT_TRANSCRIPT_VIEWER_SETTINGS.textColor,
            backgroundColor: DEFAULT_TRANSCRIPT_VIEWER_SETTINGS.backgroundColor,
        };
    if (stored.textColor === colors.textColor && stored.backgroundColor === colors.backgroundColor) {
        return stored;
    }
    return { ...stored, ...colors };
}
//# sourceMappingURL=viewer-theme.js.map